// d:\pos_web\src\pages\stock-history\StockHistoryPage.js
import React, { useMemo } from 'react';
import GenericListPage from '../GenericListPage'; // Adjusted path
import StockHistoryForm from './StockHistoryForm'; // Import the new form
import useApi from '../../hooks/useApi'; // added API hook
import { useLocation } from 'react-router-dom';

const stockHistoryColumns = [
  {
    header: 'Date',
    key: 'timestamp',
    render: (entry) => new Date(entry.timestamp).toLocaleString(),
  },
  {
    header: 'Product',
    key: 'productName', // Assuming productName and productSku are available on the entry
    render: (entry) => `${entry.productName || 'N/A'} (SKU: ${entry.productSku || 'N/A'})`,
  },
  {
    header: 'Change Type',
    key: 'type',
    render: (entry) => entry.type || 'N/A',
  },
  {
    header: 'Reference',
    key: 'relatedDocId',
    render: (entry) => {
      if (!entry.relatedDocId) return 'N/A';
      const ref = typeof entry.relatedDocId === 'string' ? entry.relatedDocId : entry.relatedDocId?._id || entry.relatedDocId?.id;
      return `${entry.relatedDocType || 'Doc'}: ${(ref || '').toString().substring(0, 8)}...`;
    },
  },
  {
    header: 'Qty Changed',
    key: 'quantityChanged',
    render: (entry) => (
      <span className={entry.quantityChanged >= 0 ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>
        {entry.quantityChanged > 0 ? `+${entry.quantityChanged}` : entry.quantityChanged}
      </span>
    ),
  },
  {
    header: 'Stock Before',
    key: 'stockBeforeMovement',
  },
  {
    header: 'Stock After',
    key: 'stockAfterMovement',
  },
  {
    header: 'User',
    key: 'userName', // Assuming userName is available
    render: (entry) => entry.userName || 'System',
  },
  {
    header: 'Notes',
    key: 'notes',
    render: (entry) => entry.remarks || 'N/A',
  },
];

// --- CSV helpers ---
function escapeCsv(val) {
  if (val == null) return '';
  return String(val).replace(/"/g, '""');
}
function getNested(obj, path) {
  if (!path) return '';
  return path.split('.').reduce((o, k) => (o ? o[k] : ''), obj);
}
function downloadCsvString(csvString, filename = 'stock-history.csv') {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 500);
}

const StockHistoryPage = () => {
  const api = useApi();

  const location = useLocation();

  const defaultFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      relatedDocId: params.get('relatedDocId') || '',
      type: params.get('type') || '',
    };
  }, [location.search]);
  const stockHistoryFilterConfig = useMemo(() => [
    {
      name: 'productId',
      type: 'select',
      label: 'Product',
      endpoint: 'products', // Assumes an endpoint to fetch products
      keyField: '_id',
      valueField: 'name',
    },
    {
      name: 'changeType',
      type: 'select',
      label: 'Change Type',
      options: [ // These could also be fetched from an endpoint if they are dynamic
        { value: '', label: 'All Types' },
        { value: 'SALE', label: 'Sale' },
        { value: 'STOCK_IN', label: 'Stock In' },
        { value: 'MANUAL_ADJUSTMENT', label: 'Manual Adjustment' },
        { value: 'RETURN', label: 'Return' },
        { value: 'DAMAGE', label: 'Damage' },
        { value: 'transfer_in', label: 'Transfer In' },
        { value: 'transfer_out', label: 'Transfer Out' },
      ],
      keyField: 'value', // Assuming GenericListPage handles 'value' and 'label' for options
      valueField: 'label',
    },
    { name: 'relatedDocId', type: 'text', label: 'Reference ID' },
    { name: 'startDate', type: 'date', label: 'Start Date' },
    { name: 'endDate', type: 'date', label: 'End Date' },
  ], []);

  // --- CSV export handler ---
  const handleDownloadCsv = async (currentFilters = {}) => {
    try {
      const queryParams = { ...currentFilters, noPagination: true, export: 'csv' };
      const resp = await api.get('inventory-log', { queryParams });

      // normalize possible response shapes
      let rows = [];
      if (Array.isArray(resp)) rows = resp;
      else if (Array.isArray(resp?.data)) rows = resp.data;
      else if (Array.isArray(resp?.data?.data)) rows = resp.data.data;
      else rows = [];

      if (!rows.length) {
        alert('No stock history found for selected filters.');
        return;
      }

      // define CSV columns (match stockHistoryColumns keys)
      const cols = [
        { header: 'Date', key: 'timestamp', transform: (r) => r.timestamp ? new Date(r.timestamp).toISOString() : '' },
        { header: 'Product', key: 'productName', transform: (r) => `${r.productName || ''}${r.productSku ? ` (SKU:${r.productSku})` : ''}` },
        { header: 'Change Type', key: 'type' },
        { header: 'Reference', key: 'relatedDocId', transform: (r) => r.relatedDocId ? `${r.relatedDocType || 'Doc'}:${String(r.relatedDocId).substring(0,8)}` : '' },
        { header: 'Qty Changed', key: 'quantityChanged' },
        { header: 'Stock Before', key: 'stockBeforeMovement' },
        { header: 'Stock After', key: 'stockAfterMovement' },
        { header: 'User', key: 'userName' },
        { header: 'Notes', key: 'remarks' },
      ];

      const header = cols.map(c => `"${escapeCsv(c.header)}"`).join(',');
      const body = rows.map(row => cols.map(col => {
        let v = '';
        if (typeof col.transform === 'function') v = col.transform(row);
        else if (col.key) v = getNested(row, col.key);
        return `"${escapeCsv(v ?? '')}"`;
      }).join(',')).join('\r\n');

      const csvString = `${header}\r\n${body}`;
      const filename = `stock-history-${new Date().toISOString().slice(0,10)}.csv`;
      downloadCsvString(csvString, filename);
    } catch (err) {
      console.error('Stock history CSV export failed', err);
      alert('Unable to export stock history CSV. Check console for details.');
    }
  };

  return (
    <GenericListPage
      title="Stock Inventory History"
      // Permission for viewing history. Adding new movements might need another permission check within the form or GenericListPage.
      permission="view_inventory_history"
      entityEndpoint="inventory-log" // API endpoint for fetching stock history/log
      columns={stockHistoryColumns}
      FormComponent={StockHistoryForm} // Use StockHistoryForm for "Add New"
      filterConfig={stockHistoryFilterConfig}
      defaultFilters={defaultFilters}
      onDownloadCsv={handleDownloadCsv} // added CSV export
      // Note: GenericListPage will also use StockHistoryForm for "Edit".
      // StockHistoryForm is designed to reset to an "add" state, effectively
      // preventing edits of historical data through this UI.
    />
  );
};

export default StockHistoryPage;